import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { ChevronLeft, ChevronRight,Calendar } from "lucide-react";
import "./dashboard.css";
import { ReactComponent as UserImage } from "../../Assets/mobileUser.svg";
import {fetchAppUsageDataAPI} from "../../actioncreators/actioncreators";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DigitalWellbeingChart = ({ response,selectedMemberId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [usageData, setUsageData] = useState(null); // Store response data
  const [getresponse,setResponse] = useState(response);
  const [error, setError] = useState(null);

  // Example of using useEffect to handle API calls when currentDate changes
useEffect(() => {
  if (selectedMemberId) {
    handleLocationClick();
  }
}, [currentDate, selectedMemberId]);

  useEffect(() => {
    // Simulate API call delay
    const fetchData = async () => {
      setIsLoading(true); // Show loader
      setTimeout(() => {
        setUsageData(getresponse?.usageData); // Simulate setting data
        setIsLoading(false); // Hide loader
      }, 2000); // Simulated API delay
    };

    fetchData();
  }, [getresponse]);

  const handleLocationClick = async () => {
    const requestBody = {
      userId: selectedMemberId, // Ensure selectedMemberId is defined and set appropriately
      date: currentDate.toISOString().split("T")[0], // 'YYYY-MM-DD' format
    };

    try {
      const apiResponse = await fetchAppUsageDataAPI(requestBody); // Fetch from your API

      const { businessStatusCode } = apiResponse;

      // Check if the response contains the data as expected
      if (businessStatusCode === 2) {
        setResponse(apiResponse); // Update the response prop
      } else {
        toast.info("Tracking details not found");
        // fetchMembers(); // Ensure fetchMembers is defined
      }
    } catch (err) {
      setError(err.message); // Ensure setError is defined
    }
  };

  const handlePreviousDay = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(prevDate.getDate() - 1);
      return newDate;
    });
  };

  const handleNextDay = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(prevDate.getDate() + 1);
      return newDate;
    });
  };

  const isNextDayDisabled = () => {
    const today = new Date();
    return currentDate.toDateString() === today.toDateString();
  };

  const formatDate = (date) =>
    `${date.toLocaleString("default", {
      month: "short",
    })} ${date.getDate()}, ${date.getFullYear()}`;

  const formatTime = (milliseconds) => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours} hr ${minutes} min`;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const formattedTime = formatTime(value);

    if (formattedTime === '0 hr 0 min') {
      return null; // Do not render label if time is '0 hr 0 min'
    }

    const label = name;
    const valueLabel = `(${formattedTime})`;

    return (
      <text x={x} y={y} textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize="11" fill="#333">
        <tspan x={x} dy="-0.5em" fontWeight="1000">
          {label}
        </tspan>
        <tspan x={x} dy="1.1em" fill="#666">
          {valueLabel}
        </tspan>
      </text>
    );
  };

  const totalScreenTime = formatTime(usageData?.totalScreenTime);
  const chartData = usageData?.appUsageStats
    ?.filter(app => app.usageTime > 1 * 60 * 1000) // Only include apps with usageTime > 5 minutes
    .map(app => ({
      name: app.appName,
      value: app.usageTime,
    }));

  const appColorMapping = {
    WhatsApp: "#25d366",
    Snapchat: "#FFFC00",
    YouTube: "#FF0000",
    Instagram: "#E1306C",
    Facebook: "#1877F2",
    Twitter: "#1DA1F2",
    Spotify: "#1db954",
    geolocation_attendence_tracking: "#fd5f07",
    LinkedIn: "#0A66C2",
    TikTok: "#010101",
    Messenger: "#0084FF",
    Reddit: "#FF4500",
    Pinterest: "#E60023",
    Slack: "#4A154B",
    Gmail: "#D14836",
    Zoom: "#2D8CFF",
    MicrosoftTeams: "#6264A7",
    Discord: "#5865F2",
    Dropbox: "#007EE5",
  };

  const generateColors = (appNames) => {
    const defaultColors = [
      "#003f5c",
      "#2f4b7c",
      "#665191",
      "#a05195",
      "#d45087",
      "#f95d6a",
      "#ff7c43",
      "#ffa600",
    ];
    let defaultColorIndex = 0;

    return appNames?.map((name) => {
      if (appColorMapping[name]) {
        return appColorMapping[name];
      }
      const color = defaultColors[defaultColorIndex % defaultColors.length];
      defaultColorIndex++;
      return color;
    });
  };

  const colors = generateColors(chartData?.map((app) => app.name));

  const CustomDateInput = ({ value, onClick }) => (
    <div className="date-picker-wrapper" style={{ position: "relative", display: "inline-block" }}>
      <input
        type="text"
        value={value}
        onClick={onClick}
        readOnly
        className="text-sm font-bold p-2 bg-gray-200 rounded"
        style={{ paddingRight: "30px" }} // Ensure space for the icon
      />
      <Calendar
        onClick={onClick} // Corrected the onClick event handler
        className="calendar-icon"
        style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: "pointer",
          color: "#555",
        }}
      />
    </div>
  );

  return (
    <div className="container">
      <div className="row m-0 px-4">
        <div className="col-12 col-md-12 ms-3">
          <span className="text-sm font-bold">Member Daily Usage</span>
          <button
            onClick={handlePreviousDay}
            className="p-2  rounded-full ml-5"
            style={{color: '#fc5d08',border:'1px solid #fc5d08'}}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={handleNextDay}
            className="p-2  rounded-full m-3"
            style={{color: '#fc5d08',border:'1px solid #fc5d08'}}
            disabled={isNextDayDisabled()}
            aria-disabled={isNextDayDisabled()}
          >
            <ChevronRight size={16} />
          </button>
          <DatePicker
            selected={currentDate}
            onChange={(date) => setCurrentDate(date)}
            dateFormat="MMM d, yyyy"
            customInput={<CustomDateInput />}
            maxDate={new Date()}
          />
        </div>

        <div className="row d-flex shadow" style={styles.chartWrapper}>
          {isLoading ? (
            <div className="loader-container">
              <div className="spinner"></div>
              <p>Loading data...</p>
            </div>
          ) : usageData ? (
            <>
              <div className="col-12 col-md-8" style={{ position: "relative", display: "flex", justifyContent: "center" }}>
                <div style={{ position: "relative", width: "520px", height: "400px" }}>
                  <PieChart className="p-3" width={520} height={400}>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={100}
                      outerRadius={140}
                      fill="#8884d8"
                      paddingAngle={3}
                      dataKey="value"
                      label={renderCustomLabel}
                      labelLine={{ stroke: "#999", strokeWidth: 1 }}
                    >
                      {chartData?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={colors[index % colors.length]}
                          className="pie-cell"
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatTime(value)}
                      contentStyle={styles.tooltip}
                      itemStyle={styles.tooltipItem}
                    />
                  </PieChart>
                  <div style={styles.centerLabel}>
                    <span className="fw-bold" style={{ color: "#666", fontSize: "14px" }}>Today</span>
                    <p className="fw-bold" style={{ fontSize: "18px", margin: "4px 0 0 0", color: "#333" }}>{totalScreenTime}</p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4 bg-white" style={styles.card1}>
                <p className="fw-bold text-black">OTHER DETAILS</p>
                <div className="border-bottom p-2">
                  <span className="text-black">Screen Unlocks - </span>
                  <span className="text-black fw-bold">
                    {usageData?.screenUnlocks} Times
                  </span>
                </div>
                <div className="border-bottom p-2">
                  <span className="text-black">Notifications - </span>
                  <span className="text-black fw-bold">
                    {usageData?.notificationsReceived}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mx-auto px-5 py-3">
                <UserImage
                  alt="Image Not Found"
                  className="w-50 h-50 mx-auto px-5 py-2"
                />
                <p className="fw-bold text-center mt-3">
                  No app usage was found for the selected member on the selected
                  date.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    flexDirection: "column",
  },
  spinner: {
    border: "4px solid rgba(0, 0, 0, 0.1)",
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    borderTopColor: "#007bff",
    animation: "spin 1s linear infinite",
  },
  chartWrapper: {
    position: "relative",
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    borderRadius: "10px",
    backgroundColor: "#fff",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
  },
  centerLabel: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    pointerEvents: "none",
  },
  tooltip: {
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    borderRadius: "5px",
    zIndex: "1",
    fontSize: "10px",
    fontWeight: "bold",
  },
  tooltipItem: {
    color: "#333",
  },
  card1: {
    position: "absolute",
    left: "80%",
    top: "30%",
    transform: "translate(-50%, -50%)",
  },
};

export default DigitalWellbeingChart;
